import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, IndianRupee } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryTabs = [
  { id: 'all', label: 'All Experiences' },
  { id: 'adventure', label: 'Adventure' },
  { id: 'dining', label: 'Dining' },
  { id: 'wellness', label: 'Wellness' },
  { id: 'cultural', label: 'Cultural' },
];

const Experiences = () => {
  const [experiences, setExperiences] = useState([]);
  const [filteredExperiences, setFilteredExperiences] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExperiences();
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredExperiences(experiences);
    } else {
      setFilteredExperiences(experiences.filter(exp => exp.category === activeTab));
    }
  }, [activeTab, experiences]);

  const fetchExperiences = async () => {
    try {
      const response = await axios.get(`${API}/experiences`);
      setExperiences(response.data);
      setFilteredExperiences(response.data);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Experiences | Rudragarh Resort, Jaisalmer</title>
        <meta name="description" content="Desert adventures at Rudragarh — ATV rides on sand dunes, horse riding, archery, cultural programs, rain dance, and starlit open-air theatre near Jaisalmer." />
        <meta property="og:title" content="Desert Experiences | Rudragarh Resort" />
        <meta property="og:description" content="ATV rides, horse riding, cultural shows, rain dance, and more — unforgettable desert experiences at Rudragarh near Jaisalmer." />
        <meta property="og:url" content="https://www.rudragarh.com/experiences" />
        <meta property="og:image" content="https://www.rudragarh.com/images/story-sand.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Desert Experiences | Rudragarh Resort" />
        <meta name="twitter:description" content="ATV rides, horse riding, cultural shows, rain dance, and more — unforgettable desert experiences at Rudragarh near Jaisalmer." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/story-sand.jpeg" />
        <link rel="canonical" href="https://www.rudragarh.com/experiences" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center" data-testid="experiences-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/story-sand.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="overline">Discover</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Desert <span className="text-gold-500">Experiences</span>
            </h1>
            <p className="text-foreground/70 max-w-xl mx-auto">
              From thrilling adventures to serene wellness retreats, 
              immerse yourself in the magic of the Thar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-20 z-40 bg-void/95 backdrop-blur-md border-b border-gold-500/10 py-4">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center justify-center gap-2 md:gap-4 overflow-x-auto pb-2">
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 md:px-6 py-2 text-xs md:text-sm uppercase tracking-widest transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gold-500 text-black'
                    : 'text-foreground/60 hover:text-gold-500 border border-transparent hover:border-gold-500/30'
                }`}
                data-testid={`exp-filter-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="section-padding" data-testid="experiences-grid">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-80 bg-void-subtle animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredExperiences.map((experience, index) => (
                <motion.div
                  key={experience.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                  data-testid={`experience-card-${experience.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={experience.image}
                      alt={experience.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-gold-500/20 backdrop-blur-sm text-gold-400 text-xs uppercase tracking-wider">
                        {experience.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 bg-void-subtle border border-gold-500/10 border-t-0">
                    <h3 className="font-heading text-xl mb-2 group-hover:text-gold-500 transition-colors">
                      {experience.title}
                    </h3>
                    <p className="text-foreground/60 text-sm mb-4 line-clamp-2">
                      {experience.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <div className="flex items-center gap-2 text-foreground/50 text-sm">
                        <Clock size={16} />
                        <span>{experience.duration}</span>
                      </div>
                      {experience.price > 0 ? (
                        <div className="flex items-center gap-1 text-gold-500">
                          <IndianRupee size={16} />
                          <span className="font-heading text-lg">{experience.price.toLocaleString()}</span>
                        </div>
                      ) : (
                        <span className="text-gold-500 text-sm uppercase tracking-wider">Complimentary</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-void-paper" data-testid="experiences-cta">
        <div className="max-w-4xl mx-auto text-center container-padding">
          <span className="overline">Ready to Explore?</span>
          <h2 className="text-3xl md:text-4xl font-heading font-medium mb-6">
            Book Your Desert <span className="text-gold-500">Adventure</span>
          </h2>
          <p className="text-foreground/60 mb-8">
            All experiences can be booked during your stay. 
            Contact our concierge for personalized recommendations.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Experiences;
